import React from "react";
import cx from "classnames";
import { useSelector } from "react-redux";
import stylesV2 from "./DoctorSelectionPanelV2.module.scss";
import stylesV3 from "./DoctorSelectionPanelV3.module.scss";
import { DoctorButtonV2 } from "./DoctorButtonV2";
import { DoctorTimeSlotV2 } from "./DoctorTimeSlotV2";
import { ConnectedDoctorSlots } from "./ConnectedDoctorSlots";
import { AvailabilityForDoctorV2 } from "./AvailabilityForDoctorV2";
import { DoctorAccordion } from "features/scheduling/AppointmentSelectSlot/DoctorAccordion";
import { WaitlistV2 } from "features/scheduling/components/Waitlist/WaitlistV2";
import {
  selectFirstAvailableDatesForAllDoctorsInOffice,
  selectFirstAvailablityLoadedForOffice,
} from "features/scheduling/firstAvailabilitySlice";
import { selectNextAvailableDatesForOffice } from "features/scheduling/availabilitySummarySlice";
import {
  selectUpcomingTimeslotsForOfficeByDate,
  selectIstimeSlotLoaded,
} from "features/scheduling/timeSlotsSlice";
import { Doctor } from "types/doctor";
import { Office } from "types/office";
import { Slot } from "types/slot";
import { ExamType } from "types/examType";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { RootState } from "app/store";
import { areFirstAvailableApptsByDefaultEnabled } from "featureFlags/areFirstAvailableApptsByDefaultEnabled";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface DoctorSelectionPanelProps {
  selectedDoctorId?: string;
  doctors: Doctor[];
  selectedOffice: Office;
  forDate: string;
  visible: boolean;
  onSelection: (type: string, value: Slot) => void;
  onSelectNextAvail: (date: string) => void;
  selectedSlot?: undefined | Slot;
  weekCalendar: string[];
  loadOnce: boolean;
  onSetLoadOnce: () => void;
  onSelectWaitlist: () => void;
  newDateArr: any;
  useClassicStartLayout?: boolean;
  isMobileView?: boolean;
  onNextMobile?: () => void;
}

// TODO: scrollAmount can be set to 1 and still successfully work on any browser _except_ Safari

const DoctorSlots: React.FC<DoctorSelectionPanelProps> = ({
  doctors,
  selectedDoctorId,
  selectedOffice,
  forDate,
  visible,
  onSelection,
  onSelectNextAvail,
  selectedSlot,
  weekCalendar,
  loadOnce,
  onSetLoadOnce,
  newDateArr,
  useClassicStartLayout = false,
  isMobileView = false,
  onNextMobile,
}) => {
  const slotsByDoctor = useSelector((state: RootState) =>
    selectUpcomingTimeslotsForOfficeByDate(state, selectedOffice.id)
  );
  const isSlotLoaded = useSelector((state: RootState) => selectIstimeSlotLoaded(state));
  React.useEffect(() => {
    if (weekCalendar.length > 0 && isSlotLoaded && !loadOnce) {
      let dayStart = false;
      for (const weekDay in weekCalendar) {
        if (dayStart) break;
        if (typeof newDateArr[weekCalendar[weekDay]] !== "undefined") {
          dayStart = true;
          onSelectNextAvail(weekCalendar[weekDay]);
          onSetLoadOnce();
          break;
        }
      }
    }
  }, [
    weekCalendar,
    newDateArr,
    isSlotLoaded,
    loadOnce,
    onSelectNextAvail,
    onSetLoadOnce,
  ]);
  const { isV3 } = useSchedulingABVariant();
  const styles =
    isV3 && !useClassicStartLayout ? stylesV3 : stylesV2;
  /*
  if (isSlotLoaded && !loadOnce) {
    const doctorsDayStart = doctors.map((doctor) => {
      return Object.keys(slotsByDoctor[doctor.id])[0];
    });

    let daystart = false;
    for (let i = 0; i < weekCalendar.length; i++) {
      if (daystart) {
        break;
      }
      for (let j = 0; j < doctorsDayStart.length; j++) {
        if (daystart) break;
        if (doctorsDayStart[j] === weekCalendar[i]) {
          daystart = true;
          if (daystart) {
            onSelectNextAvail(weekCalendar[i]);
            onSetLoadOnce();
            break;
          }
        }
      }
    }
  } */
  const availabilityByDoctor = useSelector((state: RootState) =>
    selectNextAvailableDatesForOffice(
      state,
      selectedOffice.id,
      forDate,
      selectedOffice.timeSlotWeeks
    )
  );

  const firstAvailability = useSelector((state: RootState) =>
    selectFirstAvailableDatesForAllDoctorsInOffice(state, selectedOffice.id)
  );
  const firstAvailabilityLoaded = useSelector((state: RootState) =>
    selectFirstAvailablityLoadedForOffice(state, selectedOffice.id)
  );
  const getDoctorAvailability = (doctor: Doctor) => {
    if (!isSlotLoaded) return "loading";
    if (areFirstAvailableApptsByDefaultEnabled()) {
      return firstAvailabilityLoaded
        ? firstAvailability[doctor.id]
        : "loading";
    }
    return availabilityByDoctor[doctor.id];
  };

  const shouldHideOpenNetworkDoctor = (
    doctor: Doctor,
    availability: string | null
  ) =>
    doctor.offersVEH &&
    doctor.firstName === "Open Network" &&
    !availability;

  const renderConnectedDoctorSlot = (doctor: Doctor) => (
    <ConnectedDoctorSlots
      key={`doctor-cards-${doctor.id}`}
      doctor={doctor}
      officeId={selectedOffice.id}
      selectedSlot={selectedSlot}
      onSelection={onSelection}
      onNextMobile={onNextMobile}
      onSelectNextAvail={onSelectNextAvail}
      forDate={forDate}
      examType={ExamType.Contacts}
      patientReturnStatus={PatientReturnStatus.Returning}
      office={selectedOffice}
      version={2}
    />
  );

  if (isMobileView && !useClassicStartLayout) {
    const doctorsWithAvailability: React.ReactNode[] = [];
    const doctorsWithoutAvailability: React.ReactNode[] = [];

    doctors.forEach((doctor) => {
      const availability = getDoctorAvailability(doctor);

      if (shouldHideOpenNetworkDoctor(doctor, availability)) {
        return;
      }

      if (availability === null) {
        doctorsWithoutAvailability.push(renderConnectedDoctorSlot(doctor));
        return;
      }

      doctorsWithAvailability.push(renderConnectedDoctorSlot(doctor));
    });

    return (
      <>
        {doctorsWithAvailability}
          <div className={styles.borderDivider}></div>
          <WaitlistV2 office={selectedOffice} />
          <div className={styles.borderDivider}></div>
        {doctorsWithoutAvailability.length > 0 && (
          <DoctorAccordion title="Doctors with limited availability">
            {doctorsWithoutAvailability}
          </DoctorAccordion>
        )}
      </>
    );
  }

  let isAvailability = 0;
  const doctorSlotsClassName =
    isV3 && !useClassicStartLayout ? styles.doctorSlotsContainer : undefined;
  return (
    <div className={doctorSlotsClassName}>
      {doctors.map((doctor, i) => {
        const availability = getDoctorAvailability(doctor);

        if (
          doctor.offersVEH &&
          doctor.firstName === "Open Network" &&
          !availability
        ) {
          return null;
        }

        if (availability) {
          isAvailability++;
          return (
            <div
              key={doctor.id}
              className={
                !visible
                  ? i < 4
                    ? styles.doctorTimeSlotContainer
                    : cx(styles.doctorTimeSlotContainer, styles.divHidden)
                  : styles.doctorTimeSlotContainer
              }
            >
              <div className={styles.contentLeft}>
                <DoctorButtonV2
                  key={`doctor_${i}`}
                  doctor={doctor}
                  searchDate={forDate}
                  availability={availability}
                  selected={selectedDoctorId === doctor.id}
                  loaded={
                    areFirstAvailableApptsByDefaultEnabled()
                      ? firstAvailabilityLoaded
                      : availability !== "loading"
                  }
                  selectedOffice={selectedOffice}
                  isMobileView={isMobileView}
                  availabilityInfo={
                    <AvailabilityForDoctorV2
                      key={`doctor_avail_${i}`}
                      doctor={doctor}
                      office={selectedOffice}
                      forDate={forDate}
                      slots={slotsByDoctor[doctor.id]}
                      availability={availability}
                    />
                  }
                />
              </div>
              <div className={styles.contentRight}>
                <DoctorTimeSlotV2
                  key={`doctor_timeslot_${i}`}
                  searchDate={forDate}
                  doctorId={doctor.id}
                  selectedOffice={selectedOffice}
                  availability={availability}
                  onSelection={onSelection}
                  onSelectNextAvail={onSelectNextAvail}
                  selectedSlot={selectedSlot}
                  useClassicStartLayout={useClassicStartLayout}
                  isMobileView={isMobileView}
                  onNextMobile={onNextMobile}
                />
              </div>
            </div>
          );
        } else if (availability == null) {
          return (
            <div
              key={doctor.id}
              className={
                /*!visible
                  ? i < 4
                    ? styles.doctorTimeSlotContainer
                    : cx(styles.limitedAvailability, styles.divHidden)
                  : styles.limitedAvailability
                  */
                !visible && doctors.length > 4 && isAvailability > 0
                  ? cx(styles.limitedAvailability, styles.divHidden)
                  : styles.limitedAvailability
              }
            >
              <DoctorButtonV2
                key={`doctor_${i}`}
                doctor={doctor}
                selected={selectedDoctorId === doctor.id}
                selectedOffice={selectedOffice}
                isMobileView={isMobileView}
                searchDate={forDate}
                availability={availability}
                loaded={
                  areFirstAvailableApptsByDefaultEnabled()
                    ? firstAvailabilityLoaded
                    : availability !== "loading"
                }
                availabilityInfo={
                  <AvailabilityForDoctorV2
                    key={`doctor_avail_${i}`}
                    doctor={doctor}
                    office={selectedOffice}
                    forDate={forDate}
                    slots={slotsByDoctor[doctor.id]}
                    availability={availability}
                  />
                }
              />
            </div>
          );
        }
      })}
    </div>
  );
};

export const DoctorSelectionPanelV2: React.FC<DoctorSelectionPanelProps> = ({
  doctors,
  selectedOffice,
  forDate,
  visible,
  onSelection,
  onSelectNextAvail,
  selectedSlot,
  weekCalendar,
  loadOnce,
  onSetLoadOnce,
  onSelectWaitlist,
  newDateArr,
  useClassicStartLayout = false,
  isMobileView = false,
  onNextMobile,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles =
    isV3 && !useClassicStartLayout ? stylesV3 : stylesV2;
  return (
    <>
      {doctors.length > 0 ? (
        <DoctorSlots
          doctors={doctors}
          forDate={forDate}
          selectedOffice={selectedOffice}
          visible={visible}
          onSelection={onSelection}
          onSelectNextAvail={onSelectNextAvail}
          selectedSlot={selectedSlot}
          weekCalendar={weekCalendar}
          loadOnce={loadOnce}
          onSetLoadOnce={onSetLoadOnce}
          onSelectWaitlist={onSelectWaitlist}
          newDateArr={newDateArr}
          useClassicStartLayout={useClassicStartLayout}
          isMobileView={isMobileView}
          onNextMobile={onNextMobile}
        />
      ) : (
        <div>
          <div>
            <div className={styles.noAvailableDoctors}>
              <p>
                We’re sorry! No Doctors available for this office, we encourage
                you to select from other nearby offices or{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    onSelectWaitlist();
                    e.preventDefault();
                  }}
                >
                  join the waitlist.
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
